import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo, secondaryServices } from '../data';
import { Phone, Menu, X, ChevronDown, Instagram, Facebook, MapPin, Mail, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location]);

  // Lock scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const navTextColor = isScrolled || !isHomePage ? 'text-slate-900' : 'text-white';
  const navLogoColor = isScrolled || !isHomePage ? 'text-sky-500' : 'text-sky-400';
  const navBg = isScrolled 
    ? 'bg-white/95 backdrop-blur-md shadow-sm py-3 md:py-5 lg:py-3' 
    : (!isHomePage ? 'bg-white py-3 md:py-5 lg:py-3 shadow-sm' : 'bg-transparent py-6');

  const menuItems = [
    { name: 'Forside', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Fordele', path: '/fordele' },
    { name: 'Priser', path: '/priser' },
    { name: 'Galleri', path: '/galleri' },
    { name: 'Kontakt', path: '/kontakt-os' },
  ];

  const sidebarVariants = {
    closed: { x: '100%', transition: { type: 'spring', stiffness: 300, damping: 30 } },
    open: { x: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } }
  };

  const backdropVariants = {
    closed: { opacity: 0 },
    open: { opacity: 1 }
  };

  const itemVariants = {
    closed: { opacity: 0, x: 20 },
    open: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.1, duration: 0.3 }
    })
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-0 gap-4">
          <div className="flex items-center justify-between md:justify-between lg:justify-start w-full lg:w-auto relative">
            <Link 
              to="/" 
              onClick={() => {
                setMobileMenuOpen(false);
                window.scrollTo(0, 0);
              }}
              className={`flex items-center gap-3 text-2xl sm:text-3xl font-black tracking-tighter uppercase transition-colors ${navTextColor}`}
            >
              <img 
                src="https://assets.auxodev.dk/assets/c1d178a5-103b-4f78-8eea-c670ac502600/5fbcd12a-1c9b-4262-9738-f2674e3154a1.png" 
                alt="KC Detailer Logo" 
                className={`w-auto object-contain transition-all duration-300 ${isScrolled ? 'h-14' : 'h-24 md:h-28'}`}
              />
              <span className={`hidden sm:inline-block transition-all duration-300 ${isScrolled ? 'text-2xl' : 'text-3xl md:text-4xl'}`}>KC<span className={navLogoColor}>DETAILER</span></span>
            </Link>

            {/* Mobile Toggle (Only visible below lg) */}
            <div className="lg:hidden flex items-center gap-3">
              <button 
                onClick={() => setMobileMenuOpen(true)}
                className={`p-2 rounded-lg transition-colors ${navTextColor}`}
              >
                <Menu size={28} />
              </button>
            </div>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex flex-col lg:flex-row items-center gap-4 lg:gap-8 w-full lg:w-auto">
              {menuItems.map((item) => (
                <Link 
                  key={item.name + item.path}
                  to={item.path} 
                  className={`text-sm font-bold uppercase tracking-wider hover:opacity-70 transition-colors ${navTextColor} ${item.name === 'Kontakt' ? 'text-sky-500' : ''}`}
                >
                  {item.name}
                </Link>
              ))}

            <div className="flex items-center gap-4">
              <Link 
                to="/kontakt"
                className="flex items-center gap-2 bg-blue-900 text-white px-6 py-2.5 rounded-full text-sm font-bold hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20 active:scale-95 whitespace-nowrap"
              >
                Book tid nu
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={backdropVariants}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] lg:hidden"
            />

            {/* Sidebar */}
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={sidebarVariants}
              className="fixed top-0 right-0 h-full w-[85%] max-w-[400px] bg-white z-[70] shadow-2xl flex flex-col lg:hidden"
            >
              {/* Sidebar Header */}
              <div className="p-6 flex items-center justify-between border-b border-slate-100">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 text-xl font-black tracking-tighter uppercase text-slate-900">
                  <img 
                    src="https://assets.auxodev.dk/assets/c1d178a5-103b-4f78-8eea-c670ac502600/5fbcd12a-1c9b-4262-9738-f2674e3154a1.png" 
                    alt="KC Detailer Logo" 
                    className="h-8 w-auto object-contain"
                  />
                  <span>KC<span className="text-sky-500">DETAILER</span></span>
                </Link>
                <button 
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 bg-slate-100 rounded-full text-slate-600 hover:bg-slate-200 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Sidebar Content */}
              <div className="flex-1 overflow-y-auto px-6 py-8">
                <div className="space-y-6">
                  {menuItems.map((item, i) => (
                    <motion.div
                      key={item.path}
                      custom={i}
                      variants={itemVariants}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-between text-2xl font-bold text-slate-900 group"
                      >
                        {item.name}
                        <ChevronRight size={24} className="text-slate-300 group-hover:text-slate-900 transition-colors" />
                      </Link>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-12 pt-8 border-t border-slate-100">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">Populære ydelser</div>
                  <div className="grid gap-3">
                    {secondaryServices.slice(0, 3).map((service, i) => (
                      <motion.div 
                        key={service.slug}
                        custom={i + 5}
                        variants={itemVariants}
                      >
                        <Link
                          to={`/service/${service.slug}`}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors"
                        >
                          <div className="font-bold text-slate-900">{service.title}</div>
                          <div className="text-xs text-slate-500 mt-0.5">Se mere om pakken</div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="mt-12 space-y-6">
                  <div className="flex items-center gap-4 text-slate-600">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <Phone size={18} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Ring til os</div>
                      <a href={`tel:${companyInfo.phone}`} className="font-bold text-slate-900">{companyInfo.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-slate-600">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <Mail size={18} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Skriv en mail</div>
                      <a href={`mailto:${companyInfo.email}`} className="font-bold text-slate-900">{companyInfo.email}</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar Footer */}
              <div className="p-6 bg-slate-50 border-t border-slate-100">
                <a 
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center justify-center gap-3 w-full bg-blue-900 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all"
                >
                  <Phone size={20} />
                  Book tid nu
                </a>
                <div className="flex items-center justify-center gap-6 mt-6 text-slate-400">
                  <Instagram size={20} className="hover:text-slate-900 cursor-pointer transition-colors" />
                  <Facebook size={20} className="hover:text-slate-900 cursor-pointer transition-colors" />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}
